import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import ConfirmModal from "../components/ConfirmModal";
import { toast } from "sonner";
import {
  Loader2, Plus, Copy, ExternalLink, Archive, RotateCcw, Star,
  ShoppingBag, Edit3, X, Check,
} from "lucide-react";

// Phase 3L.1 (Feb 2026) — Retail Link Profiles admin.
//
// A "Link Profile" is a customer-facing storefront experience that the
// tenant can spin up multiple of. Each one ships with:
//   - identity (name, slug, description, generated display_code)
//   - one-or-more catalog bindings
//   - per-link order mode toggles
//   - experience switches (chat/sales-order/contact requirements/etc.)
//   - status (draft/published/archived) — soft-delete only
//
// The default profile is auto-created server-side; we render its
// preview URL as the tenant's existing /retail/{slug} link so the
// operator never sees an empty page.

const ORDER_MODES = [
  { key: "dine_in",  label: "Dine-In"  },
  { key: "takeout",  label: "Takeout"  },
  { key: "pickup",   label: "Pickup"   },
  { key: "delivery", label: "Delivery" },
];

const EXPERIENCE_KEYS = [
  { key: "chat_enabled",            label: "Enable Chat with us" },
  { key: "sales_order_enabled",     label: "Enable Sales Order"  },
  { key: "require_contact_info",    label: "Require contact info (Pickup/Delivery)" },
  { key: "require_table_number",    label: "Require table number (Dine-In)" },
  { key: "show_ai_recommendations", label: "Show AI recommendations" },
  { key: "allow_guest_checkout",    label: "Allow guest checkout" },
];

function tenantSlugFromUser(user) {
  // Reuse the tenant's chatbot_slug as the retail storefront slug. Same
  // contract as RetailCustomerPage / /retail/{slug}.
  return (user?.chatbot_slug || user?.company_chatbot_slug || "").trim();
}

function buildLinkUrl(slug, linkSlug, isDefault) {
  if (!slug) return "";
  const origin = window.location.origin;
  if (isDefault || !linkSlug) return `${origin}/retail/${slug}`;
  return `${origin}/retail/${slug}/${linkSlug}`;
}

function CopyBadge({ value, label, testId }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const onClick = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); }
    catch (_e) { toast.error("Copy failed"); }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200"
      title={label || "Copy"}
    >
      <span>{value}</span>
      {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

function StatusPill({ status }) {
  const map = {
    draft:     "bg-amber-50 text-amber-700 border-amber-200",
    published: "bg-emerald-50 text-emerald-700 border-emerald-200",
    archived:  "bg-slate-100 text-slate-500 border-slate-300",
  };
  return (
    <span className={"text-[10px] font-medium px-2 py-0.5 rounded-full border uppercase tracking-wider " + (map[status] || map.draft)}>
      {status}
    </span>
  );
}

export default function RetailLinkProfilesAdminPage() {
  const [profiles, setProfiles] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editor, setEditor] = useState(null); // null | { mode: "create" | "edit", data: profile? }
  const [savingId, setSavingId] = useState(null);
  const [me, setMe] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null); // Phase 3L.1 Pass B — in-app confirm replaces window.confirm

  const refresh = async (archived = includeArchived) => {
    setLoading(true);
    try {
      const [profRes, catRes, meRes] = await Promise.all([
        api.get(`/retail/link-profiles?include_archived=${archived ? "true" : "false"}`),
        api.get("/retail/catalogs"),
        api.get("/company"),
      ]);
      setProfiles(profRes?.data?.link_profiles || []);
      setCatalogs(catRes?.data?.catalogs || []);
      setMe(meRes?.data || null);
    } catch (e) {
      toast.error("Could not load link profiles.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh(includeArchived);
  }, [includeArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  const tenantSlug = me?.company?.chatbot_slug || me?.chatbot_slug || "";

  const onArchive = (p) => {
    if (p.is_default) {
      toast.error("Default profile cannot be archived. Promote another profile first.");
      return;
    }
    // Phase 3L.1 Pass B — open the in-app confirm modal instead of
    // window.confirm so the flow is keyboard/screen-reader/Playwright
    // friendly.
    setArchiveTarget(p);
  };

  const doArchive = async () => {
    const p = archiveTarget;
    if (!p) return;
    setSavingId(p.id);
    try {
      await api.post(`/retail/link-profiles/${p.id}/archive`);
      toast.success(`Archived "${p.name}"`);
      setArchiveTarget(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    } finally { setSavingId(null); }
  };

  const onRestore = async (p) => {
    setSavingId(p.id);
    try {
      await api.post(`/retail/link-profiles/${p.id}/restore`);
      toast.success(`Restored "${p.name}"`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setSavingId(null); }
  };

  const onPromoteDefault = async (p) => {
    setSavingId(p.id);
    try {
      await api.put(`/retail/link-profiles/${p.id}`, { is_default: true });
      toast.success(`"${p.name}" is now the default storefront`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not promote profile");
    } finally { setSavingId(null); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="retail-link-profiles-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-orange-500" /> Retail Link Profiles
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Spin up multiple customer-facing storefronts — Kiosk, QR Table, Pickup, Catering — all powered by the same catalogs. Your existing <code className="px-1 bg-slate-100 rounded">/retail/{tenantSlug || "<slug>"}</code> link continues to resolve the default profile.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-600 inline-flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                data-testid="retail-link-profiles-show-archived"
              />
              Show archived
            </label>
            <button
              type="button"
              onClick={() => setEditor({ mode: "create" })}
              data-testid="retail-link-profile-new-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-orange-500 text-white text-sm font-medium hover:bg-orange-600"
            >
              <Plus className="w-4 h-4" /> New Link Profile
            </button>
          </div>
        </div>

        {/* Phase 3L.3 (Feb 2026) — Assistant ↔ Retail Link Profile bindings */}
        <AssistantLinkProfilesSection profiles={profiles} />

        {/* List */}
        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>
        ) : profiles.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl px-6 py-12 text-center text-sm text-slate-500" data-testid="retail-link-profiles-empty">
            No link profiles yet. Tap <b>New Link Profile</b> to create one.
          </div>
        ) : (
          <div className="grid gap-3" data-testid="retail-link-profiles-list">
            {profiles.map((p) => {
              const url = buildLinkUrl(tenantSlug, p.link_slug, p.is_default);
              return (
                <div
                  key={p.id}
                  data-testid={`retail-link-profile-${p.id}`}
                  className={"bg-white border rounded-xl p-4 sm:p-5 " + (p.status === "archived" ? "opacity-70 border-slate-200" : "border-slate-200 hover:border-slate-300")}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-base font-semibold text-slate-900">{p.name}</h2>
                        <StatusPill status={p.status} />
                        {p.is_default && (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-orange-50 text-orange-700 border-orange-200 inline-flex items-center gap-1">
                            <Star className="w-3 h-3" /> Default
                          </span>
                        )}
                      </div>
                      {p.description && <p className="text-sm text-slate-500 mt-1">{p.description}</p>}
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <CopyBadge value={p.display_code} label="Copy link ID" testId={`retail-link-code-${p.id}`} />
                        <CopyBadge value={p.link_slug} label="Copy slug" testId={`retail-link-slug-${p.id}`} />
                        {url && (
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`retail-link-open-${p.id}`}
                            className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
                          >
                            <ExternalLink className="w-3 h-3" /> Open storefront
                          </a>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px] text-slate-500">
                        <span>{p.catalog_ids?.length || 0} catalog{(p.catalog_ids?.length || 0) === 1 ? "" : "s"}</span>
                        <span>·</span>
                        <span>{ORDER_MODES.filter((m) => p.order_modes?.[m.key]?.enabled).map((m) => m.label).join(", ") || "no modes enabled"}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {p.status !== "archived" && !p.is_default && (
                        <button
                          type="button"
                          onClick={() => onPromoteDefault(p)}
                          disabled={savingId === p.id}
                          data-testid={`retail-link-profile-set-default-${p.id}`}
                          className="text-xs px-2.5 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"
                          title="Make this the default storefront"
                        >
                          <Star className="w-3.5 h-3.5" /> Default
                        </button>
                      )}
                      {p.status !== "archived" && (
                        <button
                          type="button"
                          onClick={() => setEditor({ mode: "edit", data: p })}
                          data-testid={`retail-link-profile-edit-${p.id}`}
                          className="text-xs px-2.5 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"
                        >
                          <Edit3 className="w-3.5 h-3.5" /> Edit
                        </button>
                      )}
                      {p.status === "archived" ? (
                        <button
                          type="button"
                          onClick={() => onRestore(p)}
                          disabled={savingId === p.id}
                          data-testid={`retail-link-profile-restore-${p.id}`}
                          className="text-xs px-2.5 py-1.5 rounded border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 inline-flex items-center gap-1"
                        >
                          <RotateCcw className="w-3.5 h-3.5" /> Restore
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onArchive(p)}
                          disabled={savingId === p.id || p.is_default}
                          data-testid={`retail-link-profile-archive-${p.id}`}
                          className="text-xs px-2.5 py-1.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1"
                          title={p.is_default ? "Default profile cannot be archived. Promote another profile first." : "Archive"}
                        >
                          <Archive className="w-3.5 h-3.5" /> Archive
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {editor && (
          <LinkProfileEditor
            mode={editor.mode}
            initial={editor.data}
            catalogs={catalogs}
            onClose={() => setEditor(null)}
            onSaved={async () => { setEditor(null); await refresh(); }}
          />
        )}

        <ConfirmModal
          open={Boolean(archiveTarget)}
          title="Archive link profile?"
          message={archiveTarget ? `"${archiveTarget.name}" will be hidden from this list. You can restore it from "Show archived" at any time. No customers will lose data — orders stay intact.` : ""}
          confirmLabel="Archive"
          tone="warning"
          busy={Boolean(archiveTarget) && savingId === archiveTarget.id}
          onCancel={() => setArchiveTarget(null)}
          onConfirm={doArchive}
          testIdPrefix="retail-link-profile-archive-confirm"
        />
      </div>
    </DashboardLayout>
  );
}

// ----------------------- editor modal --------------------------------------

function LinkProfileEditor({ mode, initial, catalogs, onClose, onSaved }) {
  const isCreate = mode === "create";
  const [name, setName] = useState(initial?.name || "");
  const [linkSlug, setLinkSlug] = useState(initial?.link_slug || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [status, setStatus] = useState(initial?.status || "draft");
  const [catalogIds, setCatalogIds] = useState(initial?.catalog_ids || []);
  const [orderModes, setOrderModes] = useState(() => {
    const seed = initial?.order_modes || {};
    const out = {};
    for (const m of ORDER_MODES) out[m.key] = { enabled: Boolean(seed?.[m.key]?.enabled) };
    return out;
  });
  const [experience, setExperience] = useState(() => {
    const seed = initial?.experience || {};
    const out = {};
    for (const k of EXPERIENCE_KEYS) out[k.key] = seed[k.key] !== undefined ? Boolean(seed[k.key]) : (k.key === "chat_enabled" || k.key === "sales_order_enabled" || k.key === "allow_guest_checkout");
    return out;
  });
  const [isDefault, setIsDefault] = useState(Boolean(initial?.is_default));
  const [submitting, setSubmitting] = useState(false);

  // Auto-derive slug from name on create (only when slug untouched).
  const slugWasManuallyEdited = useMemo(() => Boolean(initial?.link_slug) || linkSlug !== "", [initial, linkSlug]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    if (!name.trim()) return toast.error("Name is required.");
    const sluggedSlug = (linkSlug || name).toLowerCase().trim().replace(/[^a-z0-9-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
    if (!sluggedSlug) return toast.error("Link slug must contain letters or digits.");
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        link_slug: sluggedSlug,
        description: description.trim(),
        catalog_ids: catalogIds,
        order_modes: orderModes,
        experience,
      };
      if (isCreate) {
        payload.status = status;
        payload.is_default = isDefault;
        await api.post("/retail/link-profiles", payload);
        toast.success("Link profile created.");
      } else {
        payload.status = status;
        payload.is_default = isDefault;
        await api.put(`/retail/link-profiles/${initial.id}`, payload);
        toast.success("Link profile updated.");
      }
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save link profile.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-3 py-8 overflow-y-auto" data-testid="retail-link-profile-editor">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}/>
      <form onSubmit={submit} className="relative z-10 w-full max-w-2xl bg-white rounded-2xl border border-slate-200 shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">
            {isCreate ? "New Link Profile" : `Edit · ${initial?.display_code || initial?.name}`}
          </h2>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-900" aria-label="Close"><X className="w-4 h-4"/></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Name *">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Kiosk"
                data-testid="retail-link-profile-name"
                className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"
              />
            </Field>
            <Field label="Link slug (URL)" hint={"Used in /retail/{tenant}/" + (linkSlug || "<slug>")}>
              <input
                value={linkSlug}
                onChange={(e) => setLinkSlug(e.target.value)}
                placeholder="kiosk"
                data-testid="retail-link-profile-slug"
                className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm font-mono"
              />
            </Field>
          </div>

          <Field label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              data-testid="retail-link-profile-description"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              placeholder="Visible only to operators."
            />
          </Field>

          <Field label="Catalogs">
            <div className="grid sm:grid-cols-2 gap-1.5 max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2">
              {catalogs.length === 0 ? (
                <div className="text-xs text-slate-500 col-span-2 py-2 text-center">No catalogs yet. Create one in <b>Retail Catalog</b>.</div>
              ) : catalogs.map((c) => (
                <label key={c.id} className="text-sm flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={catalogIds.includes(c.id)}
                    onChange={(e) => {
                      setCatalogIds((prev) => e.target.checked ? [...prev, c.id] : prev.filter((x) => x !== c.id));
                    }}
                    data-testid={`retail-link-profile-catalog-${c.id}`}
                  />
                  <span className="flex-1 truncate">{c.name}</span>
                  {c.display_code && <span className="text-[10px] font-mono text-slate-400">{c.display_code}</span>}
                </label>
              ))}
            </div>
          </Field>

          <Field label="Order modes">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {ORDER_MODES.map((m) => (
                <label key={m.key} className="text-sm flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:border-slate-300">
                  <input
                    type="checkbox"
                    checked={Boolean(orderModes[m.key]?.enabled)}
                    onChange={(e) => setOrderModes({ ...orderModes, [m.key]: { ...(orderModes[m.key]||{}), enabled: e.target.checked } })}
                    data-testid={`retail-link-profile-mode-${m.key}`}
                  />
                  {m.label}
                </label>
              ))}
            </div>
          </Field>

          <Field label="Experience">
            <div className="grid sm:grid-cols-2 gap-1.5">
              {EXPERIENCE_KEYS.map((k) => (
                <label key={k.key} className="text-sm flex items-center gap-2 px-2 py-1 rounded">
                  <input
                    type="checkbox"
                    checked={Boolean(experience[k.key])}
                    onChange={(e) => setExperience({ ...experience, [k.key]: e.target.checked })}
                    data-testid={`retail-link-profile-exp-${k.key}`}
                  />
                  {k.label}
                </label>
              ))}
            </div>
          </Field>

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Status">
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid="retail-link-profile-status"
                className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </Field>
            <Field label="Default storefront">
              <label className="text-sm flex items-center gap-2 h-10">
                <input
                  type="checkbox"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  data-testid="retail-link-profile-is-default"
                />
                Make this profile the default for <code className="px-1 bg-slate-100 rounded">/retail/&lt;tenant&gt;</code>
              </label>
            </Field>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="retail-link-profile-cancel"
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            data-testid="retail-link-profile-save"
            className="px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 disabled:opacity-60 inline-flex items-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin"/>}
            {isCreate ? "Create profile" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 3L.3 (Feb 2026) — Assistant ↔ Retail Link Profile bindings
//
// Sits ABOVE the catalog list. Lets an operator bind a chatbot/assistant
// to a Retail Link Profile (which itself owns one-or-more catalogs).
// The legacy per-catalog "Assistant ↔ Catalog Links" panel inside the
// catalog detail view is intentionally left in place — the public chat
// resolver prefers profile bindings and falls back to the legacy
// catalog binding so live beta traffic never breaks.
// ---------------------------------------------------------------------------

function AssistantLinkProfilesSection({ profiles }) {
  const [assistants, setAssistants] = useState([]);
  const [bindings, setBindings] = useState([]);
  const [selectedAssistant, setSelectedAssistant] = useState("");
  const [selectedProfile, setSelectedProfile] = useState("");
  const [busy, setBusy] = useState(false);

  // Profiles available for binding: draft + published only (archived
  // hidden), sorted by created_at — matches the agreed scope (3a).
  const availableProfiles = useMemo(() => {
    return (profiles || [])
      .filter((p) => p.status !== "archived")
      .slice()
      .sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
  }, [profiles]);

  const load = async () => {
    try {
      const [a, l] = await Promise.all([
        api.get("/assistants").catch(() => ({ data: { assistants: [] } })),
        api.get("/retail/assistant-link-profiles").catch(() => ({ data: { links: [] } })),
      ]);
      setAssistants(a.data?.assistants || a.data?.items || []);
      setBindings(l.data?.links || []);
    } catch { /* non-critical */ }
  };

  useEffect(() => { load(); }, []);

  const bind = async () => {
    if (!selectedAssistant || !selectedProfile) return;
    setBusy(true);
    try {
      await api.post("/retail/assistant-link-profiles", {
        assistant_id: selectedAssistant,
        link_profile_id: selectedProfile,
      });
      toast.success("Linked assistant to link profile");
      setSelectedAssistant(""); setSelectedProfile("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed");
    } finally { setBusy(false); }
  };

  const unbind = async (binding_id) => {
    setBusy(true);
    try {
      await api.delete(`/retail/assistant-link-profiles/${binding_id}`);
      toast.success("Unlinked");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlink failed");
    } finally { setBusy(false); }
  };

  const assistantName = (id) => {
    const a = assistants.find((x) => x.id === id);
    return a?.assistant_name || a?.name || id;
  };
  const profileLabel = (id) => {
    const p = (profiles || []).find((x) => x.id === id);
    if (!p) return id;
    const code = p.display_code ? ` (${p.display_code})` : "";
    return `${p.name}${code}`;
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-4 space-y-3"
      data-testid="assistant-link-profile-section"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-medium text-sm text-slate-900">Chat Assistant on storefronts</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Each binding lets a chatbot answer from a specific storefront experience. The public chat picks the profile&#39;s first <em>published</em> catalog.
          </div>
        </div>
      </div>

      {assistants.length === 0 ? (
        <div className="text-xs text-slate-500" data-testid="assistant-link-profile-no-assistants">
          No assistants available yet. Create one under Resources &amp; AI Assistant.
        </div>
      ) : availableProfiles.length === 0 ? (
        <div className="text-xs text-slate-500" data-testid="assistant-link-profile-no-profiles">
          No link profiles available. Create one above first.
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <select
            data-testid="assistant-link-profile-assistant-select"
            value={selectedAssistant}
            onChange={(e) => setSelectedAssistant(e.target.value)}
            className="h-9 rounded-lg border border-slate-200 px-2 text-sm bg-white"
          >
            <option value="">— choose assistant —</option>
            {assistants.map((a) => (
              <option key={a.id} value={a.id}>{a.assistant_name || a.name || a.id}</option>
            ))}
          </select>
          <select
            data-testid="assistant-link-profile-profile-select"
            value={selectedProfile}
            onChange={(e) => setSelectedProfile(e.target.value)}
            className="h-9 rounded-lg border border-slate-200 px-2 text-sm bg-white"
          >
            <option value="">— choose link profile —</option>
            {availableProfiles.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.display_code ? ` (${p.display_code})` : ""}
                {p.status === "draft" ? " · DRAFT" : ""}
                {p.is_default ? " · default" : ""}
              </option>
            ))}
          </select>
          <button
            data-testid="assistant-link-profile-bind-btn"
            onClick={bind}
            disabled={!selectedAssistant || !selectedProfile || busy}
            className="h-9 px-3 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-60"
          >
            Link
          </button>
        </div>
      )}

      {bindings.length === 0 ? (
        <div className="text-xs text-slate-500" data-testid="assistant-link-profile-empty">
          No assistant ↔ link-profile bindings yet.
        </div>
      ) : (
        <ul className="space-y-1 text-sm" data-testid="assistant-link-profile-list">
          {bindings.map((b) => (
            <li
              key={b.id}
              data-testid={`assistant-link-profile-row-${b.id}`}
              className="flex items-center justify-between bg-slate-50 rounded-md px-3 py-1.5 gap-2"
            >
              <span className="truncate">
                <span className="font-medium">{assistantName(b.assistant_id)}</span>
                <span className="text-slate-400 mx-2">→</span>
                <span>{profileLabel(b.link_profile_id)}</span>
              </span>
              <button
                onClick={() => unbind(b.id)}
                disabled={busy}
                className="text-xs text-slate-500 hover:text-red-600 disabled:opacity-50"
                data-testid={`assistant-link-profile-unbind-${b.id}`}
              >
                Unlink
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}



function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}
